import React, {useEffect, useMemo, useState} from 'react';
import {connect} from "react-redux";
import {Alert, Box, Button, Chip, FormControl, InputLabel, MenuItem, Paper, Select, Skeleton, Snackbar, Stack, Typography} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import moment from "moment-timezone";
import {heuresActions} from "../../modules/heures/heures.actions.js";
import {heuresConstants} from "../../modules/heures/heures.constants.js";
import {helper} from "../../utils/helper.js";
import EquipageDetailModal from "./rh/heures/EquipageDetailModal.js";
import OperateurList from "./rh/validation/OperateurList.js";
import SemaineBlock from "./rh/validation/SemaineBlock.js";
import ConfirmValidateAllDialog from "./rh/validation/ConfirmValidateAllDialog.js";
import {formatMinutes, getStatutGlobal, minutesToDecimal} from "./rh/validation/validationHelpers.js";

function ValidationSkeleton() {
    return (
        <Box display="flex" sx={{height: "calc(100vh - 220px)"}}>
            <Box sx={{width: 280, flexShrink: 0, backgroundColor: "#f7f7f8", borderRight: "1px solid #e0e0e0", p: 1.5}}>
                {Array.from({length: 8}).map((_, i) => (
                    <Box key={i} sx={{display: "flex", alignItems: "center", gap: 1, mb: 1.5}}>
                        <Skeleton variant="circular" width={28} height={28}/>
                        <Skeleton variant="text" sx={{flex: 1}}/>
                    </Box>
                ))}
            </Box>
            <Box flex={1} sx={{p: 2}}>
                <Skeleton variant="text" width={240} height={36} sx={{mb: 2}}/>
                {Array.from({length: 3}).map((_, i) => (
                    <Skeleton key={i} variant="rounded" height={90} sx={{mb: 1.5}}/>
                ))}
            </Box>
        </Box>
    );
}

function RHValidationHeuresPage({dispatch, ...props}) {
    const [selectedPeriodeId, setSelectedPeriodeId] = useState(() => {
        const stored = localStorage.getItem("selectedPeriodeId");
        return stored ? Number(stored) : null;
    });
    const [selectedOperateurId, setSelectedOperateurId] = useState(null);
    const [validating, setValidating] = useState(null);
    const [togglingDeplacementKey, setTogglingDeplacementKey] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedPosteOperateurId, setSelectedPosteOperateurId] = useState(null);
    const [selectedStatutJournee, setSelectedStatutJournee] = useState(null);
    const [confirmValidateAllOpen, setConfirmValidateAllOpen] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);

    const data = props.heuresHebdoRh;
    const operateurs = data?.operateurs || [];

    // Tri "file d'attente" : anomalies d'abord, puis à valider, les validés en bas
    const STATUT_RANK = {anomalie: 0, a_valider: 1, valide: 2};
    const sortedOperateurs = useMemo(
        () => [...operateurs].sort((a, b) =>
            (STATUT_RANK[getStatutGlobal(a.semaines)] ?? 1) - (STATUT_RANK[getStatutGlobal(b.semaines)] ?? 1)
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [operateurs]
    );

    useEffect(() => { dispatch(heuresActions.fetchPeriodePaies()); }, [dispatch]);

    useEffect(() => {
        if (!selectedPeriodeId && props.periodePaies?.length > 0) {
            const stored = localStorage.getItem("selectedPeriodeId");
            setSelectedPeriodeId(stored ? Number(stored) : props.periodePaies[0].id);
        }
    }, [props.periodePaies, selectedPeriodeId]);

    useEffect(() => {
        if (selectedPeriodeId) {
            dispatch(heuresActions.fetchHeuresHebdoRh(selectedPeriodeId)).catch(() => {});
        }
    }, [selectedPeriodeId, dispatch]);

    useEffect(() => {
        if (operateurs.length > 0) {
            const stillExists = operateurs.some(o => o.operateur.id === selectedOperateurId);
            if (!stillExists) setSelectedOperateurId(operateurs[0].operateur.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const selectedOperateur = useMemo(
        () => operateurs.find(o => o.operateur.id === selectedOperateurId) || null,
        [operateurs, selectedOperateurId]
    );

    // Refresh silencieux : ne déclenche pas le loader global (on passe par SUCCESS directement)
    const refreshData = () => {
        heuresActions.fetchHeuresHebdoRhSilent(selectedPeriodeId)(dispatch).catch(() => {});
    };

    const onSelectedPeriodeId = (id) => {
        if (id) { localStorage.setItem("selectedPeriodeId", id); setSelectedPeriodeId(id); }
    };

    const sortedPeriodes = () => Array.isArray(props.periodePaies)
        ? props.periodePaies.filter(p => p.startDate && !isNaN(new Date(p.startDate).getTime()))
            .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
        : [];

    const selectedPeriode = props.periodePaies?.find(p => p.id === selectedPeriodeId) || null;

    const handleToggleDeplacement = (semaine) => {
        if (togglingDeplacementKey) return;
        const operateurId = selectedOperateur.operateur.id;
        const newDeplacement = !semaine.deplacement;
        // Optimistic
        setTogglingDeplacementKey(semaine.debut);
        dispatch({type: heuresConstants.HEURES_HEBDO_RH_TOGGLE_DEPLACEMENT_OPTIMISTIC, payload: {operateurId, semaineDebut: semaine.debut, deplacement: newDeplacement}});
        const action = semaine.deplacement
            ? heuresActions.deleteDeplacement(semaine.deplacementId)
            : heuresActions.createDeplacement(operateurId, semaine.debut);
        dispatch(action)
            .catch(() => {
                // Rollback
                dispatch({type: heuresConstants.HEURES_HEBDO_RH_TOGGLE_DEPLACEMENT_OPTIMISTIC, payload: {operateurId, semaineDebut: semaine.debut, deplacement: semaine.deplacement}});
                setErrorMsg("Échec de la mise à jour du forfait déplacement.");
            })
            .finally(() => setTogglingDeplacementKey(null));
    };

    const handleValidate = (semaine) => {
        if (validating) return;
        const operateurId = selectedOperateur.operateur.id;
        // Optimistic
        setValidating(semaine.debut);
        dispatch({type: heuresConstants.HEURES_HEBDO_RH_VALIDATE_OPTIMISTIC, payload: {operateurId, semaineDebut: semaine.debut}});
        dispatch(heuresActions.createValidationSemaine(operateurId, semaine.debut))
            .catch(() => {
                // Rollback
                dispatch({type: heuresConstants.HEURES_HEBDO_RH_DEVALIDATE_OPTIMISTIC, payload: {operateurId, semaineDebut: semaine.debut}});
                setErrorMsg("Échec de la validation de la semaine.");
            })
            .finally(() => setValidating(null));
    };

    const handleDevalidate = (semaine) => {
        if (!semaine.validation?.id || validating) return;
        const operateurId = selectedOperateur.operateur.id;
        // Optimistic
        setValidating(semaine.debut);
        dispatch({type: heuresConstants.HEURES_HEBDO_RH_DEVALIDATE_OPTIMISTIC, payload: {operateurId, semaineDebut: semaine.debut}});
        dispatch(heuresActions.deleteValidationSemaine(semaine.validation.id))
            .catch(() => {
                // Rollback
                dispatch({type: heuresConstants.HEURES_HEBDO_RH_VALIDATE_OPTIMISTIC, payload: {operateurId, semaineDebut: semaine.debut}});
                setErrorMsg("Échec de l'annulation de la validation.");
            })
            .finally(() => setValidating(null));
    };

    const handleValidateAll = () => {
        const toValidate = (selectedOperateur?.semaines || []).filter(s => s.statutValidation !== "valide");
        if (!toValidate.length) return;
        const operateurId = selectedOperateur.operateur.id;
        // Optimistic : tout marquer validé immédiatement
        toValidate.forEach(s => {
            dispatch({type: heuresConstants.HEURES_HEBDO_RH_VALIDATE_OPTIMISTIC, payload: {operateurId, semaineDebut: s.debut}});
        });
        // Appels API séquentiels, refresh unique à la fin, puis passage au prochain opérateur
        const doNext = (idx) => {
            if (idx >= toValidate.length) { refreshData(); selectNextOperateur(); return; }
            const s = toValidate[idx];
            setValidating(s.debut);
            dispatch(heuresActions.createValidationSemaine(operateurId, s.debut))
                .then(() => doNext(idx + 1))
                .catch(() => { setErrorMsg("Échec : certaines semaines n'ont pas pu être validées."); refreshData(); })
                .finally(() => setValidating(null));
        };
        doNext(0);
    };

    // Prochain opérateur non validé dans l'ordre de la file
    const nextOperateur = sortedOperateurs.find(
        o => o.operateur.id !== selectedOperateurId && getStatutGlobal(o.semaines) !== "valide"
    ) || null;

    const selectNextOperateur = () => {
        if (nextOperateur) setSelectedOperateurId(nextOperateur.operateur.id);
    };

    const handleDayClick = (jour) => {
        if (!jour?.posteOperateurId) return;
        setSelectedPosteOperateurId(jour.posteOperateurId);
        setSelectedStatutJournee(jour.statutJournee);
        setModalOpen(true);
    };

    const onFdtUpdate = (values) => {
        if (values.id) {
            dispatch(heuresActions.updateFeuilleDeTemps(values.id, values))
                .then(() => refreshData()).catch(e => console.error(e));
        }
    };

    const onFdtValidate = (fdtIds = []) => {
        const now = new Date().toISOString();
        Promise.all(fdtIds.map(id => dispatch(heuresActions.updateFeuilleDeTemps(id, {id, validatedAt: now}))))
            .then(() => { setModalOpen(false); refreshData(); }).catch(e => console.error(e));
    };

    const isAdmin = helper.getCurrentUser()?.roles?.includes("ROLE_ADMIN");

    // Résumé mensuel
    const nbValidees = selectedOperateur?.semaines?.filter(s => s.statutValidation === "valide").length || 0;
    const nbTotal = selectedOperateur?.semaines?.length || 0;
    const allValidated = nbTotal > 0 && nbValidees === nbTotal;
    const periodeMois = selectedPeriode ? moment(selectedPeriode.startDate).format("MMMM YYYY") : "";

    // Résumé de ce que "Tout valider" s'apprête à signer
    const semainesAValider = selectedOperateur?.semaines?.filter(s => s.statutValidation !== "valide") || [];
    const resumeValidation = {
        nbSemaines: semainesAValider.length,
        totalMinutes: semainesAValider.reduce((acc, s) => acc + (s.totaux?.total || 0) * 60, 0),
        hsMinutes: semainesAValider.reduce((acc, s) => acc + (s.totaux?.hsMinutes || 0), 0),
        nbAlertes: semainesAValider.reduce((acc, s) => acc + (s.alertes?.length || 0), 0),
    };

    return (
        <>
            <Typography variant="h4" gutterBottom>Validation des heures</Typography>

            {/* Sélecteur de période */}
            <Box mb={2} maxWidth={480}>
                <FormControl fullWidth>
                    <InputLabel id="periode-select-label">Période de paie</InputLabel>
                    <Select
                        labelId="periode-select-label"
                        label="Période de paie"
                        value={selectedPeriodeId || ""}
                        onChange={(e) => onSelectedPeriodeId(e.target.value)}
                        renderValue={() => selectedPeriode
                            ? `${selectedPeriode.libelle} (${helper.formatDate(selectedPeriode.startDate)} - ${helper.formatDate(selectedPeriode.endDate)})`
                            : "Sélectionnez une période"
                        }
                    >
                        {sortedPeriodes().map(p => (
                            <MenuItem key={p.id} value={p.id}>
                                {p.libelle} ({helper.formatDate(p.startDate)} - {helper.formatDate(p.endDate)})
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Layout deux colonnes */}
            {props.loading && operateurs.length === 0 ? (
            <ValidationSkeleton/>
            ) : (
            <Box display="flex" sx={{height: "calc(100vh - 220px)", gap: 0}}>
                {/* Sidebar opérateurs */}
                <Box sx={{
                    width: 280, flexShrink: 0,
                    backgroundColor: "#f7f7f8",
                    borderRight: "1px solid #e0e0e0",
                    overflowY: "auto",
                }}>
                    <OperateurList
                        operateurs={sortedOperateurs}
                        selectedOperateurId={selectedOperateurId}
                        onSelect={setSelectedOperateurId}
                    />
                </Box>

                {/* Panneau principal */}
                <Box flex={1} sx={{overflowY: "auto", backgroundColor: "#fff", px: 2, pt: 1.5}}>
                    {selectedOperateur ? (
                        <>
                            {/* Résumé mensuel */}
                            <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1.5}>
                                <Box>
                                    <Stack direction="row" alignItems="center" gap={1}>
                                        <Typography variant="h5" fontWeight={700}>{selectedOperateur.operateur.libelle}</Typography>
                                        {selectedOperateur.operateur.overtimeMinutes > 0 && (
                                            <Typography variant="body2" color="warning.dark" fontWeight={600}>
                                                +{minutesToDecimal(selectedOperateur.operateur.overtimeMinutes)}h HS ce mois
                                            </Typography>
                                        )}
                                    </Stack>
                                </Box>
                                {allValidated ? (
                                    <Stack direction="row" alignItems="center" gap={1}>
                                        <Chip icon={<CheckIcon/>} label="Tout validé" color="success" variant="outlined"/>
                                        {nextOperateur && (
                                            <Button variant="outlined" endIcon={<ArrowForwardIcon/>} onClick={selectNextOperateur}>
                                                Suivant : {nextOperateur.operateur.libelle}
                                            </Button>
                                        )}
                                    </Stack>
                                ) : (
                                    <Button
                                        variant="contained"
                                        color="success"
                                        onClick={() => setConfirmValidateAllOpen(true)}
                                        disabled={!!validating}
                                    >
                                        Tout valider
                                    </Button>
                                )}
                            </Box>

                            {/* Cartes semaines */}
                            {(selectedOperateur.semaines || []).map(semaine => (
                                <SemaineBlock
                                    key={semaine.debut}
                                    semaine={semaine}
                                    onDayClick={handleDayClick}
                                    onToggleDeplacement={handleToggleDeplacement}
                                    onValidate={handleValidate}
                                    onDevalidate={handleDevalidate}
                                    isAdmin={isAdmin}
                                    validating={validating}
                                    togglingDeplacementKey={togglingDeplacementKey}
                                />
                            ))}
                        </>
                    ) : (
                        <Paper variant="outlined" sx={{p: 2}}>
                            <Typography variant="body2" color="textSecondary">
                                Sélectionnez un opérateur pour afficher ses semaines.
                            </Typography>
                        </Paper>
                    )}
                </Box>
            </Box>
            )}

            <ConfirmValidateAllDialog
                open={confirmValidateAllOpen}
                operateurLibelle={selectedOperateur?.operateur?.libelle}
                periodeMois={periodeMois}
                resume={resumeValidation}
                onCancel={() => setConfirmValidateAllOpen(false)}
                onConfirm={() => { setConfirmValidateAllOpen(false); handleValidateAll(); }}
            />

            <Snackbar
                open={!!errorMsg}
                autoHideDuration={6000}
                onClose={() => setErrorMsg(null)}
                anchorOrigin={{vertical: "bottom", horizontal: "center"}}
            >
                <Alert severity="error" variant="filled" onClose={() => setErrorMsg(null)}>
                    {errorMsg}
                </Alert>
            </Snackbar>

            <EquipageDetailModal
                posteOperateurId={selectedPosteOperateurId}
                open={modalOpen}
                handleClose={() => setModalOpen(false)}
                onFdtUpdate={onFdtUpdate}
                onFdtValidate={onFdtValidate}
                onRefresh={refreshData}
                initialStatutJournee={selectedStatutJournee}
                onStatutJourneeChange={(s) => setSelectedStatutJournee(s)}
            />
        </>
    );
}

const mapStateToProps = (state) => ({
    heuresHebdoRh: state.heures.heuresHebdoRh,
    periodePaies: state.heures.periodePaies,
    loading: state.heures.loading,
    error: state.heures.error,
});

export default connect(mapStateToProps)(RHValidationHeuresPage);
